import { addMinutes, distanceInWordsStrict, format, subMinutes } from 'date-fns';
import { formatToTimeZone } from 'date-fns-timezone';
import { uniq } from 'lodash';

import { DateTimeFormat } from 'common/components/TimeDisplay/TimeDisplay';

export enum TimeZone {
  UTC = 'Etc/UTC',
}

export const getTimeZone = () => {
  let timeZone;

  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || TimeZone.UTC;
  } catch (err) {
    // in case older browsers throw an error for browser API
    timeZone = TimeZone.UTC;
  }

  return timeZone as TimeZone;
};

export const TIMEZONES = [
  'US/Mountain',
  'US/Eastern',
  'America/Jamaica',
  'US/Arizona',
  'CET',
  'Pacific/Midway',
  'US/Alaska',
  'US/Hawaii',
  'US/Pacific',
  'US/Central',
  'US/East-Indiana',
  'America/Puerto_Rico',
  'America/Juneau',
  'Canada/Newfoundland',
  'America/Sao_Paulo',
  'Atlantic/Cape_Verde',
  'Etc/GMT+12',
  'GMT',
  'Etc/UTC',
  'Europe/Amsterdam',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Helsinki',
  'Europe/London',
  'Europe/Kiev',
  'Africa/Cairo',
  'Africa/Addis_Ababa',
  'MET',
  'Asia/Dubai',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Dhaka',
  'Asia/Bangkok',
  'Asia/Ho_Chi_Minh',
  'Asia/Taipei',
  'Asia/Tokyo',
  'Australia/Darwin',
  'Australia/Brisbane',
  'Australia/Lindeman',
  'Pacific/Guadalcanal',
  'Pacific/Auckland',
];

export const getAvailableTimeZones = (initialTimezone?: TimeZone) => {
  // as our list of timezones is reduced to a bare min, we additionally include browser local timezone
  // and an optional initial timezone (one previously saved, which may not be in the list)
  return uniq([getTimeZone(), ...((initialTimezone && [initialTimezone, ...TIMEZONES]) || TIMEZONES)]).sort();
};

export const formatUTCDateTime = (date: string, dateTimeFormat: string = DateTimeFormat.STANDARD) => {
  const timeZone = getTimeZone();
  return formatToTimeZone(date, `${dateTimeFormat} z`, { timeZone });
};

export type FormatDistanceInWordsOptions = {
  addSuffix?: boolean;
  unit?: 's' | 'm' | 'h' | 'd' | 'M' | 'Y';
  partialMethod?: 'floor' | 'ceil' | 'round';
  locale?: Record<string, unknown>;
  truncate?: boolean;
};

export const formatDistanceInWordsStrict = (from: Date, to: Date, options?: FormatDistanceInWordsOptions) => {
  const words = distanceInWordsStrict(from, to, options);

  if (options?.truncate) {
    const [dist, desc] = words.split(' ');

    return dist + desc.charAt(0);
  }

  return words;
};

export function convertToUTC(date: Date) {
  if (date.getTimezoneOffset() < 0) {
    return addMinutes(date, Math.abs(date.getTimezoneOffset()));
  }
  if (date.getTimezoneOffset() > 0) {
    return subMinutes(date, Math.abs(date.getTimezoneOffset()));
  }
  return date;
}

export function convertFromUTC(date: Date) {
  if (date.getTimezoneOffset() < 0) {
    return subMinutes(date, Math.abs(date.getTimezoneOffset()));
  }
  if (date.getTimezoneOffset() > 0) {
    return addMinutes(date, Math.abs(date.getTimezoneOffset()));
  }
  return date;
}

export function formatToUTCWithoutOffset(date: string, newFormat?: string) {
  return `${format(convertFromUTC(new Date(date)), newFormat ?? 'MMM Do YYYY HH:mm')} UTC`;
}

export const HOUR = 60 * 60 * 1000;
export const DAY = 24 * HOUR;
